<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  Sun,
  Moon,
  Monitor,
  Check,
  Palette,
  LayoutGrid,
  Languages,
  RotateCcw,
} from 'lucide-vue-next'
import Card from '@/components/ui/Card.vue'
import Badge from '@/components/ui/Badge.vue'
import {
  useAppearanceStore,
  type AccentColor,
  type Density,
} from '@/stores/appearance'
import { useThemeStore, type ThemeMode } from '@/stores/theme'
import {
  useAutoRetry,
  AUTO_RETRY_DEFAULTS,
  AUTO_RETRY_RANGES,
} from '@/composables/useAutoRetry'

const { t } = useI18n()
const appearance = useAppearanceStore()
const theme = useThemeStore()
const autoRetry = useAutoRetry()

interface ThemeOption {
  value: ThemeMode
  label: string
  description: string
  icon: typeof Sun
}

const themeOptions: ThemeOption[] = [
  {
    value: 'light',
    label: 'theme.light',
    description: 'settings.appearance.themeLightHint',
    icon: Sun,
  },
  {
    value: 'dark',
    label: 'theme.dark',
    description: 'settings.appearance.themeDarkHint',
    icon: Moon,
  },
  {
    value: 'auto',
    label: 'theme.auto',
    description: 'settings.appearance.themeAutoHint',
    icon: Monitor,
  },
]

interface AccentOption {
  value: AccentColor
  label: string
  hue: string
}

const accentOptions: AccentOption[] = [
  { value: 'blue',   label: 'Blue',   hue: '217 91% 60%' },
  { value: 'green',  label: 'Green',  hue: '158 64% 45%' },
  { value: 'purple', label: 'Purple', hue: '271 81% 60%' },
  { value: 'orange', label: 'Orange', hue: '24 95% 53%'  },
  { value: 'pink',   label: 'Pink',   hue: '330 81% 60%' },
]

interface DensityOption {
  value: Density
  label: string
  description: string
}

const densityOptions: DensityOption[] = [
  {
    value: 'comfortable',
    label: 'settings.appearance.comfortable',
    description: 'settings.appearance.comfortableHint',
  },
  {
    value: 'compact',
    label: 'settings.appearance.compact',
    description: 'settings.appearance.compactHint',
  },
]

interface LangOption {
  value: 'zh-CN' | 'en' | 'auto'
  label: string
  hint: string
}

const langOptions: LangOption[] = [
  { value: 'auto',  label: 'settings.appearance.langAuto',  hint: 'settings.appearance.langAutoHint' },
  { value: 'zh-CN', label: 'settings.appearance.langZh',    hint: 'settings.appearance.langZhHint' },
  { value: 'en',    label: 'settings.appearance.langEn',    hint: 'settings.appearance.langEnHint' },
]

const currentAccent = computed(() => appearance.accent)
const currentDensity = computed(() => appearance.density)
const currentLang = computed(() => appearance.langPref)
const currentMode = computed(() => theme.mode)

function onMaxRetriesChange(raw: string): void {
  const n = Number(raw)
  if (!Number.isFinite(n)) return
  autoRetry.updateConfig({ maxRetries: Math.round(n) })
}
function onBackoffChange(raw: string): void {
  const n = Number(raw)
  if (!Number.isFinite(n)) return
  autoRetry.updateConfig({ backoffSeconds: Math.round(n) })
}
function onConfirmWindowChange(raw: string): void {
  const n = Number(raw)
  if (!Number.isFinite(n)) return
  autoRetry.updateConfig({ confirmWindowSeconds: Math.round(n) })
}
</script>

<template>
  <div class="space-y-6">
    <!-- Theme card -->
    <Card :title="t('settings.appearance.theme')" :subtitle="t('settings.appearance.themeHint')">
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <button
          v-for="opt in themeOptions"
          :key="opt.value"
          type="button"
          class="group flex flex-col items-start gap-2 rounded-lg border p-4 text-left transition-colors"
          :class="
            currentMode === opt.value
              ? 'border-brand-500 bg-brand-500/10'
              : 'border-border bg-bg-surface hover:border-border-strong'
          "
          @click="appearance.setTheme(opt.value)"
        >
          <div class="flex w-full items-center justify-between">
            <component
              :is="opt.icon"
              :size="18"
              :class="currentMode === opt.value ? 'text-brand-500' : 'text-fg-muted'"
            />
            <Check
              v-if="currentMode === opt.value"
              :size="14"
              class="text-brand-500"
            />
          </div>
          <span class="text-sm font-medium text-fg">{{ t(opt.label) }}</span>
          <span class="text-xs text-fg-muted">{{ t(opt.description) }}</span>
        </button>
      </div>
    </Card>

    <!-- Accent color card -->
    <Card :title="t('settings.appearance.accent')" :subtitle="t('settings.appearance.accentHint')">
      <div class="flex flex-wrap items-center gap-4">
        <button
          v-for="opt in accentOptions"
          :key="opt.value"
          type="button"
          class="group flex flex-col items-center gap-2"
          @click="appearance.setAccent(opt.value)"
        >
          <span
            class="flex h-10 w-10 items-center justify-center rounded-full ring-offset-2 ring-offset-bg-surface transition-shadow"
            :class="currentAccent === opt.value ? 'ring-2 ring-fg' : 'ring-0'"
            :style="{ backgroundColor: `hsl(${opt.hue})` }"
            :aria-label="opt.label"
          >
            <Check v-if="currentAccent === opt.value" :size="14" class="text-white" />
          </span>
          <span class="text-xs text-fg-muted">{{ opt.label }}</span>
        </button>
      </div>
    </Card>

    <!-- Density card -->
    <Card :title="t('settings.appearance.density')" :subtitle="t('settings.appearance.densityHint')">
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <button
          v-for="opt in densityOptions"
          :key="opt.value"
          type="button"
          class="flex flex-col items-start gap-2 rounded-lg border p-4 text-left transition-colors"
          :class="
            currentDensity === opt.value
              ? 'border-brand-500 bg-brand-500/10'
              : 'border-border bg-bg-surface hover:border-border-strong'
          "
          @click="appearance.setDensity(opt.value)"
        >
          <div class="flex w-full items-center justify-between">
            <LayoutGrid :size="16" :class="currentDensity === opt.value ? 'text-brand-500' : 'text-fg-muted'" />
            <Check v-if="currentDensity === opt.value" :size="14" class="text-brand-500" />
          </div>
          <span class="text-sm font-medium text-fg">{{ t(opt.label) }}</span>
          <span class="text-xs text-fg-muted">{{ t(opt.description) }}</span>
        </button>
      </div>
    </Card>

    <!-- Language card -->
    <Card :title="t('settings.appearance.language')" :subtitle="t('settings.appearance.languageHint')">
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <button
          v-for="opt in langOptions"
          :key="opt.value"
          type="button"
          class="flex flex-col items-start gap-2 rounded-lg border p-4 text-left transition-colors"
          :class="
            currentLang === opt.value
              ? 'border-brand-500 bg-brand-500/10'
              : 'border-border bg-bg-surface hover:border-border-strong'
          "
          @click="appearance.setLangPref(opt.value)"
        >
          <div class="flex w-full items-center justify-between">
            <Languages :size="16" :class="currentLang === opt.value ? 'text-brand-500' : 'text-fg-muted'" />
            <Check v-if="currentLang === opt.value" :size="14" class="text-brand-500" />
          </div>
          <span class="text-sm font-medium text-fg">{{ t(opt.label) }}</span>
          <span class="text-xs text-fg-muted">{{ t(opt.hint) }}</span>
        </button>
      </div>
    </Card>

    <p class="flex items-center gap-2 text-xs text-fg-muted">
      <Palette :size="12" />
      {{ t('settings.appearance.persistHint') }}
      <Badge variant="outline" size="sm">localStorage</Badge>
    </p>

    <!-- Auto-retry card -->
    <Card
      :title="t('autoRetry.settings.title')"
      :subtitle="t('autoRetry.settings.description')"
    >
      <template #actions>
        <button
          type="button"
          class="inline-flex items-center gap-1 rounded-md border border-border bg-bg-elevated px-2 py-1 text-xs text-fg-muted hover:bg-bg-hover hover:text-fg"
          @click="autoRetry.updateConfig({ ...AUTO_RETRY_DEFAULTS })"
        >
          <RotateCcw :size="11" />
          {{ t('autoRetry.settings.reset') }}
        </button>
      </template>
      <div class="space-y-4">
        <!-- Master toggle -->
        <label class="flex cursor-pointer items-center justify-between gap-3">
          <span class="text-sm text-fg">{{ t('autoRetry.settings.enabled') }}</span>
          <span
            role="switch"
            tabindex="0"
            :aria-checked="autoRetry.config.enabled"
            class="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors"
            :class="autoRetry.config.enabled ? 'bg-brand-500' : 'bg-bg-elevated'"
            @click="autoRetry.updateConfig({ enabled: !autoRetry.config.enabled })"
            @keydown.enter.prevent="autoRetry.updateConfig({ enabled: !autoRetry.config.enabled })"
            @keydown.space.prevent="autoRetry.updateConfig({ enabled: !autoRetry.config.enabled })"
          >
            <span
              class="absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform"
              :class="autoRetry.config.enabled ? 'translate-x-4' : 'translate-x-0.5'"
            />
          </span>
        </label>

        <!-- Numeric fields (disabled when master toggle is off) -->
        <div
          class="grid grid-cols-1 gap-3 sm:grid-cols-3"
          :class="autoRetry.config.enabled ? '' : 'pointer-events-none opacity-50'"
        >
          <label class="flex flex-col gap-1">
            <span class="text-xs text-fg-muted">{{ t('autoRetry.settings.maxRetries') }}</span>
            <input
              type="number"
              :min="AUTO_RETRY_RANGES.maxRetries.min"
              :max="AUTO_RETRY_RANGES.maxRetries.max"
              :value="autoRetry.config.maxRetries"
              class="w-full rounded-md border border-border bg-bg-base px-2 py-1.5 text-sm text-fg outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500"
              @change="onMaxRetriesChange(($event.target as HTMLInputElement).value)"
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-xs text-fg-muted">{{ t('autoRetry.settings.backoffSeconds') }}</span>
            <input
              type="number"
              :min="AUTO_RETRY_RANGES.backoffSeconds.min"
              :max="AUTO_RETRY_RANGES.backoffSeconds.max"
              :value="autoRetry.config.backoffSeconds"
              class="w-full rounded-md border border-border bg-bg-base px-2 py-1.5 text-sm text-fg outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500"
              @change="onBackoffChange(($event.target as HTMLInputElement).value)"
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-xs text-fg-muted">{{ t('autoRetry.settings.confirmWindowSeconds') }}</span>
            <input
              type="number"
              :min="AUTO_RETRY_RANGES.confirmWindowSeconds.min"
              :max="AUTO_RETRY_RANGES.confirmWindowSeconds.max"
              :value="autoRetry.config.confirmWindowSeconds"
              class="w-full rounded-md border border-border bg-bg-base px-2 py-1.5 text-sm text-fg outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500"
              @change="onConfirmWindowChange(($event.target as HTMLInputElement).value)"
            />
          </label>
        </div>

        <p class="flex items-center gap-2 text-xs text-fg-muted">
          <Badge variant="outline" size="sm">afd-auto-retry</Badge>
          {{ t('autoRetry.settings.hint') }}
        </p>
      </div>
    </Card>
  </div>
</template>
